import React, { useEffect, useRef, useState } from 'react';
import { Building2, KeyRound, Lock, MoreVertical, Shield, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAccessTheme } from '../useAccessTheme';
import { FeatureBadgeChip, ProviderStatusBadge } from './ProviderBadges';
import EntraIcon from './EntraIcon';
import type { IdentityProviderKind, IdentityProviderRecord } from './identityProviderTypes';

const PROVIDER_ICONS: Record<Exclude<IdentityProviderKind, 'entra'>, LucideIcon> = {
  'active-directory': Building2,
  okta: Shield,
  oidc: KeyRound,
  saml: Lock,
  scim: Users,
};

interface IdentityProviderCardProps {
  provider: IdentityProviderRecord;
  onManage: () => void;
  onSyncNow: () => void;
  onDisconnect: () => void;
  syncing?: boolean;
  disconnecting?: boolean;
}

function formatCount(value: number | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString();
}

const IdentityProviderCard: React.FC<IdentityProviderCardProps> = ({
  provider,
  onManage,
  onSyncNow,
  onDisconnect,
  syncing = false,
  disconnecting = false,
}) => {
  const t = useAccessTheme();
  const Icon = provider.kind === 'entra' ? null : PROVIDER_ICONS[provider.kind];
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <div className={`rounded-xl border p-4 ${t.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${provider.kind === 'entra' ? '' : t.iconBg}`}>
            {provider.kind === 'entra' ? <EntraIcon className="w-8 h-8" /> : Icon ? <Icon className="w-5 h-5" /> : null}
          </div>
          <div className="min-w-0">
            <h4 className={`text-sm font-semibold ${t.title}`}>{provider.name}</h4>
            {provider.secondaryLabel ? (
              <p className={`text-xs mt-0.5 ${t.muted}`}>{provider.secondaryLabel}</p>
            ) : null}
          </div>
        </div>
        <ProviderStatusBadge status={provider.status} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <div>
          <p className={`text-xs ${t.muted}`}>Directory Sync</p>
          <p className={`text-sm font-medium mt-1 ${t.title}`}>{formatCount(provider.userCount)} Users</p>
          <p className={`text-xs mt-0.5 ${t.body}`}>{formatCount(provider.groupCount)} Groups</p>
        </div>
        <div>
          <p className={`text-xs ${t.muted}`}>Auto Sync</p>
          <p className={`text-sm font-medium mt-1 ${t.title}`}>
            {provider.autoSync ? 'Enabled' : 'Disabled'}
          </p>
        </div>
        <div>
          <p className={`text-xs ${t.muted}`}>Last Sync</p>
          <p className={`text-sm font-medium mt-1 ${t.title}`}>{provider.lastSync || '—'}</p>
        </div>
        <div>
          <p className={`text-xs ${t.muted}`}>SSO</p>
          <p className={`text-sm font-medium mt-1 ${t.title}`}>
            {provider.ssoEnabled ? 'Enabled' : 'Disabled'}
          </p>
        </div>
      </div>

      {provider.features.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {provider.features.map((feature) => (
            <FeatureBadgeChip key={feature} label={feature} />
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-2 mt-4">
        <button
          type="button"
          onClick={onManage}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${t.btnQuiet}`}
        >
          Manage
        </button>
        <button
          type="button"
          onClick={onSyncNow}
          disabled={syncing}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${t.btnQuiet} ${syncing ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
        <div className="relative ml-auto" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            disabled={disconnecting}
            className={`p-1.5 rounded-md ${t.btnGhost} ${disconnecting ? 'opacity-60 cursor-not-allowed' : ''}`}
            aria-label="More actions"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen ? (
            <div
              className={`absolute right-0 mt-1 w-40 rounded-lg border shadow-lg overflow-hidden z-10 ${t.panel}`}
            >
              <button
                type="button"
                disabled={disconnecting}
                onClick={() => {
                  setMenuOpen(false);
                  onDisconnect();
                }}
                className={`w-full text-left px-3 py-2 text-sm ${t.body} ${t.actionHover} ${disconnecting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default IdentityProviderCard;
